import { db, storage } from '../firebase/config';
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  getDoc, 
  doc, 
  query, 
  where, 
  getDocs, 
  setDoc, 
  serverTimestamp,
  collection as firestoreCollection, 
  deleteDoc 
} from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { useAuthContext } from '../hooks/useAuthContext';
import { Plus, Trash2, Heart } from 'lucide-react';
import AddProduct from '../components/AddProduct';
import Footer from '../components/Footer';
import Hero from '../components/Hero';
import FeaturedProduct from '../components/FeaturedProducts';
import './Collections.css';

export default function CollectionsPage() {
  const { collectionId } = useParams();
  const [products, setProducts] = useState([]);
  const [collection, setCollection] = useState(null);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuthContext();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        const idTokenResult = await user.getIdTokenResult();
        setIsAdmin(idTokenResult.claims?.admin || false);
      }
    };
    checkAdminStatus();
  }, [user]);

  // Fetch collection and products
  useEffect(() => {
    const fetchCollectionAndProducts = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Fetch collection details
        const collectionDocRef = doc(db, 'collections', collectionId);
        const collectionDoc = await getDoc(collectionDocRef);
        
        if (!collectionDoc.exists()) {
          throw new Error('Collection not found');
        }
        
        setCollection({ id: collectionDoc.id, ...collectionDoc.data() });

        // Fetch products
        const productsCollectionRef = firestoreCollection(db, 'products');
        const q = query(
          productsCollectionRef,
          where('collectionId', '==', collectionId)
        );
        
        const querySnapshot = await getDocs(q);
        const productsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setProducts(productsData);
      } catch (err) {
        console.error('Error fetching collection data:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    if (collectionId) {
      fetchCollectionAndProducts();
    }
  }, [collectionId]);

  // Delete product handler
  const handleDeleteProduct = async (productId, images) => {
    if (!isAdmin) {
      alert('You do not have permission to delete products.');
      return;
    }

    // Confirm deletion
    const confirmDelete = window.confirm('Are you sure you want to delete this product?');
    if (!confirmDelete) return;

    try {
      // Delete images from storage
      await Promise.all(
        images.map(async (imageUrl) => {
          // Create a reference to the image in storage
          const imageRef = ref(storage, imageUrl);
          await deleteObject(imageRef);
        })
      );

      // Delete product document from Firestore
      await deleteDoc(doc(db, 'products', productId));

      // Update local state to remove the deleted product
      setProducts(prevProducts => 
        prevProducts.filter(product => product.id !== productId)
      );
    } catch (error) {
      console.error('Error deleting product:', error);
      alert('Failed to delete product. Please try again.');
    }
  };

    // Add a new function to handle saving/unsaving products
    const handleSaveProduct = async (product) => {
      if (!user) {
        alert('Please log in or sign up to save products');
        return;
      }
  
      try {
        const savedProductRef = doc(
          db, 
          'savedProducts', 
          user.uid, 
          'items', 
          product.id
        );
  
        // Check if the product is already saved
        const savedProductSnapshot = await getDoc(savedProductRef);
        
        if (savedProductSnapshot.exists()) {
          // If product exists, remove it (unsave)
          await deleteDoc(savedProductRef);
          alert('Product removed from saved items');
        } else {
          // Save the product
          await setDoc(savedProductRef, {
            ...product,
            savedAt: serverTimestamp()
          });
          alert('Product saved successfully');
        }
      } catch (error) {
        console.error('Error saving/unsaving product:', error);
        alert('Failed to save/unsave product. Please try again.');
      }
    };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner-overlay">
        <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error">
        <p>Error: {error}</p>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="not-found">
        <p>Collection not found</p>
      </div>
    );
  }

  return (
    <>
      <Hero />
      <div className="collections-page">
        <div className="collections-container">
          <div className="collections-header">
            <h1 className="collection-name">{collection.name}</h1>
            {isAdmin && (
              <button
                onClick={() => setShowAddProduct(!showAddProduct)}
                className="add-product-button"
              >
                <Plus className="plus-icon" />
                {showAddProduct ? 'Cancel' : 'Add Product'}
              </button>
            )}
          </div>
          
          {showAddProduct && (
            <div className="add-product">
              <AddProduct collectionId={collectionId} />
            </div>
          )}

          {products.length === 0 ? (
            <p className="no-products">No products found in this collection.</p>
          ) : (
            <div className="product-grid">
              {products.map(product => (
                <div key={product.id} className="product-card-container">
                  <Link
                    to={`/product/${product.id}`}
                    className="product-link"
                  >
                    <div className="product-image-container">
                      {product.images && product.images.length > 0 ? (
                        <>
                          <img
                            src={product.images[0]} // First image
                            alt={product.name}
                            className="first-image"
                          />
                          {product.images.length > 1 && (
                            <img
                              src={product.images[1]} // Second image for hover
                              alt={product.name}
                              className="second-image"
                            />
                          )}
                        </>
                      ) : (
                        <img
                          src="/path-to-placeholder-image.jpg" // Fallback image in case no images are available
                          alt={product.name}
                          className="first-image"
                        />
                      )}
                    </div>
                    <div className="product-info">
                      <h2 className="product-name">{product.name}</h2>
                      <p className="product-price">&#8358;{product.price?.toFixed(2)}</p>
                    </div>
                  </Link>
                  {/* Add save icon */}
                  <button
                    className="save-product-button"
                    onClick={(e) => {
                      e.preventDefault(); // Prevent link navigation
                      handleSaveProduct(product);
                    }}
                  >
                    <Heart className="save-icon" />
                  </button>
                  {isAdmin && (
                    <button
                      className="delete-product-button"
                      onClick={() => handleDeleteProduct(product.id, product.images)}
                    >
                      <Trash2 className="delete-icon" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
          <FeaturedProduct />
        </div>
      </div>
      <Footer />
    </>
  );
}